import useSSE from '../useSSE';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { printElementFitted } from '../utils/printFit';

const STATUS_COLORS = {
  not_started:'#8A93A6', planning:'#2563EB', in_progress:'#5B5BD6',
  escalated:'#DC2626', at_risk:'#D97706', on_hold:'#8A93A6',
  completed:'#16A34A', cancelled:'#6B7280'
};

function getProgressColor(p) {
  if (p >= 100) return '#16A34A';
  if (p >= 60)  return '#5B5BD6';
  if (p >= 30)  return '#D97706';
  return '#DC2626';
}

export default function WBSPage() {
  const { spaceId } = useParams();
  // Back to dashboard
  const backToDash = () => navigate(spaceId ? `/spaces/${spaceId}` : '/spaces');

  const silentRef = React.useRef(false);
  useSSE(spaceId, {
    task_updated: () => { if(!silentRef.current){ silentRef.current=true; load().finally(()=>{ silentRef.current=false; }); } },
  });

  // Silent reload when phase access changes (permission granted/revoked)
  React.useEffect(() => {
    function onAccessChanged() {
      silentRef.current = true;
      load().finally(() => { silentRef.current = false; });
    }
    window.addEventListener('trajectory:access_changed', onAccessChanged);
    return () => window.removeEventListener('trajectory:access_changed', onAccessChanged);
  }, [spaceId]);
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  // expandedIds: set of ids that are expanded
  const [fullscreen, setFullscreen] = React.useState(false);
  const [printMenu, setPrintMenu] = React.useState(false);
  const storageKey = `wbs_expanded_${spaceId}`;
  const lockKey    = `wbs_locked_${spaceId}`;
  const [locked, setLocked] = useState(() => {
    try { return localStorage.getItem(lockKey) === '1'; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : { root: true };
    } catch { return { root: true }; }
  });

  // Save expansion state whenever it changes
  React.useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(expanded)); } catch {}
  }, [expanded, storageKey]);

  useEffect(() => { load(); }, [spaceId]);
  // ── Silent background refresh ─────────────────────────────────────────────
  useEffect(()=>{
    let lastTs=null, active=true, fails=0;
    const poll=async()=>{
      if(!active||!spaceId) return;
      try {
        const r=await api.get(`/spaces/${spaceId}/last-updated`).catch(()=>null);
        if(!r){fails++;if(fails>4)active=false;return;}
        fails=0;
        if(lastTs===null){lastTs=r.last_updated;return;}
        if(r.last_updated!==lastTs){
          lastTs=r.last_updated;
          load();
        }
      } catch(_){fails++;}
    };
    const iv=setInterval(poll,15000);
    return()=>{active=false;clearInterval(iv);};
  },[spaceId]);

  async function load() {
    try {
      const space   = await api.get(`/spaces/${spaceId}`);
      const folders = await api.get(`/spaces/${spaceId}/folders`);
      const foldersWithData = await Promise.all(folders.map(async folder => {
        const lists = await api.get(`/folders/${folder.id}/lists`).catch(() => []);
        const listsWithTasks = await Promise.all(lists.map(async list => {
          const tasks = await api.get(`/lists/${list.id}/tasks`).catch(() => []);
          const topLevel = tasks.filter(t => !t.parent_task_id);
          let contribution = 0;
          topLevel.forEach(t => {
            const w = t.task_weight || 0;
            const p = Math.min(100, t.calculatedProgress ?? t.progress ?? 0);
            contribution += w * p / 100;
          });
          return { ...list, tasks: topLevel, progress: Math.min(100, Math.max(0, contribution)) };
        }));
        let phaseContribution = 0;
        listsWithTasks.forEach(l => { phaseContribution += (l.weight||0) * l.progress / 100; });
        return { ...folder, lists: listsWithTasks, progress: Math.min(100, Math.max(0, phaseContribution)) };
      }));
      let projectContribution = 0;
      foldersWithData.forEach(f => { projectContribution += (f.weight||0) * f.progress / 100; });
      setTree({ ...space, folders: foldersWithData, progress: Math.min(100, Math.max(0, projectContribution)) });
      // Only reset to default if nothing is saved in localStorage yet
      const savedState = localStorage.getItem(storageKey);
      if (!savedState) {
        setExpanded({ root: true });
      }
      // If locked, do NOT touch expanded state at all — keep what was saved
    } catch(e) { console.error(e); }
  }

  function exportWBSSvg() {
    // Find the WBS tree container and wrap in SVG
    const container = document.getElementById('wbs-tree-container');
    if (!container) return;
    // Use foreignObject SVG to capture full HTML tree
    const w = container.scrollWidth + 80;
    const h = container.scrollHeight + 80;
    const html = container.outerHTML;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">
      <style>
        * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; box-sizing: border-box; }
        .wbs-node { background: white; border-radius: 10px; padding: 12px 16px; }
      </style>
      <foreignObject width="${w}" height="${h}">
        <div xmlns="http://www.w3.org/1999/xhtml" style="padding:40px;background:#F8F9FC;min-height:${h}px">
          ${html}
        </div>
      </foreignObject>
    </svg>`;
    const blob = new Blob([svg], {type:'image/svg+xml'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `WBS_${new Date().toISOString().slice(0,10)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printWBSFitted(paper, orientation, fitMode) {
    const container = document.getElementById('wbs-tree-container');
    if (!container) return;
    setPrintMenu(false);
    printElementFitted({
      el: container,
      title: `${tree?.name || 'Project'} — Work Breakdown Structure`,
      subtitle: 'Space Communication Technologies',
      paper, orientation, fitMode,
    });
  }

  function printWBS() {
    const container = document.getElementById('wbs-tree-container');
    if (!container) return;
    const name = tree?.name || 'Project';
    const date = new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'});
    const html = `<!DOCTYPE html><html><head><title>WBS - ${name}</title>
<style>
  body{font-family:Arial,sans-serif;margin:0;padding:16px;background:#fff}
  h1{font-size:18px;margin:0 0 4px}
  div.sub{font-size:11px;color:#888;margin-bottom:16px}
  *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;box-sizing:border-box;}
  svg line,svg path{stroke:#CBD5E1;stroke-width:2px;}
  div[style*="background:#CBD5E1"],div[style*="background: #CBD5E1"]{background:#CBD5E1!important;}
  div[style*="border-top"]{border-top-color:#CBD5E1!important;}
  @media print{.no-print{display:none}@page{size:A2 landscape;margin:6mm}}
</style></head><body>
<button class='no-print' onclick='window.print()' style='position:fixed;top:10px;right:10px;padding:8px 18px;background:#5B5BD6;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;font-weight:700;z-index:999'>🖨️ Print / Save as PDF</button>
<h1>${name} — Work Breakdown Structure</h1>
<div class='sub'>Exported: ${date} · Space Communication Technologies</div>
${container.outerHTML}
</body></html>`;
    const wbsIframe = document.createElement('iframe');
    wbsIframe.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;border:none;z-index:99999;background:white';
    document.body.appendChild(wbsIframe);
    wbsIframe.contentDocument.open();
    wbsIframe.contentDocument.write(html);
    wbsIframe.contentDocument.close();
    setTimeout(() => {
      wbsIframe.contentWindow.focus();
      wbsIframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(wbsIframe), 2000);
    }, 800);
  }

  function toggle(id) {
    if (locked) return;
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }

  if (!tree) return (
    <div className="page-enter" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--muted)',gap:'10px'}}>
      <div style={{width:16,height:16,border:'2px solid var(--border)',borderTopColor:'var(--brand)',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
      Building WBS…
    </div>
  );

  const projectExpanded = !!expanded['root'];

  return (
    <div style={{
      position: fullscreen ? 'fixed' : 'relative',
      top: fullscreen ? 0 : 'auto',
      left: fullscreen ? 0 : 'auto',
      right: fullscreen ? 0 : 'auto',
      bottom: fullscreen ? 0 : 'auto',
      zIndex: fullscreen ? 9999 : 'auto',
      flex: fullscreen ? 'none' : 1,
      overflow:'auto',
      background:'#F8F9FC',
      padding:'32px',
      width: fullscreen ? '100vw' : 'auto',
      height: fullscreen ? '100vh' : 'auto',
    }}>
      {/* ← Back to Overview */}
      <button onClick={backToDash}
        style={{position:'fixed',top:10,left:200,zIndex:200,
          padding:'4px 12px',background:'white',border:'1px solid #E2E8F0',
          borderRadius:'16px',cursor:'pointer',fontSize:'11px',fontWeight:'600',
          color:'#64748B',boxShadow:'0 1px 3px rgba(0,0,0,.1)',
          display:'flex',alignItems:'center',gap:'4px',lineHeight:'1.4',
          transition:'all .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.background='#5B5BD6';e.currentTarget.style.color='white';e.currentTarget.style.borderColor='#5B5BD6';}}
        onMouseLeave={e=>{e.currentTarget.style.background='white';e.currentTarget.style.color='#64748B';e.currentTarget.style.borderColor='#E2E8F0';}}>
        ← Overview
      </button>
      <div style={{marginBottom:'24px',display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
        <div>
          <h2 style={{fontSize:'18px',fontWeight:'800',color:'#1A1D23',margin:0}}>Work Breakdown Structure</h2>
          <p style={{fontSize:'12px',color:'#8A93A6',marginTop:'4px'}}>
            Project → Phase → List → Task hierarchy · Click +/− to expand/collapse
            {locked && <span style={{marginLeft:'8px',color:'#16A34A',fontWeight:'700'}}>🔒 View Locked</span>}
          </p>
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:'8px'}}>
          <button onClick={()=>{
              const newLocked = !locked;
              setLocked(newLocked);
              try { localStorage.setItem(lockKey, newLocked?'1':'0'); } catch {}
            }}
            style={{padding:'6px 14px',background:locked?'rgba(22,163,74,0.1)':'var(--surface)',border:`1px solid ${locked?'#16A34A':'var(--border)'}`,borderRadius:'var(--radius)',cursor:'pointer',fontSize:'12px',fontWeight:'700',color:locked?'#16A34A':'var(--muted)',transition:'all .2s'}}>
            {locked ? '🔒 Locked' : '🔓 Lock View'}
          </button>
          <button onClick={()=>setFullscreen(f=>!f)}
            style={{padding:'6px 12px',background:fullscreen?'#1A1D23':'var(--surface)',border:`1px solid ${fullscreen?'#1A1D23':'var(--border)'}`,borderRadius:'var(--radius)',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:fullscreen?'#fff':'var(--text)'}}>
            {fullscreen ? '✕ Exit Fullscreen' : '⛶ Fullscreen'}
          </button>
          <button onClick={exportWBSSvg}
            style={{padding:'6px 12px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>
            📄 Export SVG
          </button>
          <div style={{position:'relative',display:'inline-block'}}>
            <button onClick={()=>setPrintMenu(m=>!m)}
              style={{padding:'6px 12px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:'var(--text)'}}>
              🖨️ Print / PDF ▾
            </button>
            {printMenu && (
              <>
                <div onClick={()=>setPrintMenu(false)} style={{position:'fixed',inset:0,zIndex:998}}/>
                <div style={{position:'absolute',top:'110%',right:0,zIndex:999,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'10px',boxShadow:'0 8px 24px rgba(0,0,0,0.15)',padding:'6px',minWidth:'230px'}}>
                  <div style={{fontSize:'10px',fontWeight:'700',color:'var(--muted)',textTransform:'uppercase',letterSpacing:'0.5px',padding:'6px 10px 4px'}}>Fit to one page</div>
                  {[['A4','landscape','A4 Landscape'],['A4','portrait','A4 Portrait'],['A3','landscape','A3 Landscape'],['A3','portrait','A3 Portrait']].map(([p,o,label])=>(
                    <button key={p+o} onClick={()=>printWBSFitted(p,o,'page')}
                      style={{display:'block',width:'100%',textAlign:'left',padding:'8px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'12.5px',color:'var(--text)',borderRadius:'6px'}}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                      onMouseLeave={e=>e.currentTarget.style.background='none'}>
                      📄 {label}
                    </button>
                  ))}
                  <div style={{height:'1px',background:'var(--border)',margin:'4px 0'}}/>
                  <button onClick={()=>printWBSFitted('A3','landscape','width')}
                    style={{display:'block',width:'100%',textAlign:'left',padding:'8px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'12.5px',color:'var(--text)',borderRadius:'6px'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                    onMouseLeave={e=>e.currentTarget.style.background='none'}>
                    📐 Fit width (A3, flows down)
                  </button>
                  <button onClick={()=>{setPrintMenu(false);printWBS();}}
                    style={{display:'block',width:'100%',textAlign:'left',padding:'8px 10px',background:'none',border:'none',cursor:'pointer',fontSize:'12.5px',color:'var(--muted)',borderRadius:'6px'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface2)'}
                    onMouseLeave={e=>e.currentTarget.style.background='none'}>
                    🖨️ Full size (A2)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tree container — horizontal scroll so nothing hides under sidebar */}
      <div style={{overflowX:'auto',overflowY:'visible',width:'100%',paddingBottom:'40px'}}>
      <div id="wbs-tree-container" style={{display:'flex',flexDirection:'column',alignItems:'center',minWidth:'max-content',padding:'0 20px'}}>

        {/* PROJECT node */}
        <WBSNode
          id="root" label={tree.name} progress={tree.progress}
          color={tree.color||'#5B5BD6'} level="project" icon="🏢"
          expanded={projectExpanded} hasChildren={tree.folders.length>0}
          onToggle={()=>toggle('root')}
        />

        {/* PHASES */}
        {projectExpanded && tree.folders.length > 0 && (
          <>
            <Connector height={28}/>
            <HorizontalGroup>
              {tree.folders.map((folder, fi) => {
                const phaseExpanded = !!expanded[folder.id];
                return (
                  <TreeColumn key={folder.id}>
                    <Connector height={20}/>
                    <WBSNode
                      id={folder.id} label={folder.name} progress={folder.progress}
                      color={folder.color||'#D97706'} level="phase" icon="📁"
                      sub={`${folder.lists.length} lists`} weight={folder.weight||0}
                      expanded={phaseExpanded} hasChildren={folder.lists.length>0}
                      onToggle={()=>toggle(folder.id)}
                    />

                    {/* LISTS */}
                    {phaseExpanded && folder.lists.length > 0 && (
                      <>
                        <Connector height={16}/>
                        <HorizontalGroup>
                          {folder.lists.map(list => {
                            const listExpanded = !!expanded[list.id];
                            return (
                              <TreeColumn key={list.id}>
                                <Connector height={14}/>
                                <WBSNode
                                  id={list.id} label={list.name} progress={list.progress}
                                  color={list.color||'#0891B2'} level="list" icon="🎯"
                                  sub={`${list.tasks.length} tasks`} weight={list.weight||0}
                                  expanded={listExpanded} hasChildren={list.tasks.length>0}
                                  onToggle={()=>toggle(list.id)}
                                  onClick={()=>navigate(`/lists/${list.id}`)}
                                />

                                {/* TASKS */}
                                {listExpanded && list.tasks.length > 0 && (
                                  <>
                                    <Connector height={12}/>
                                    <HorizontalGroup>
                                      {list.tasks.map(task => {
                                        const taskExpanded = !!expanded[task.id];
                                        const hasSubs = (task.subtasks||[]).length > 0;
                                        return (
                                          <TreeColumn key={task.id}>
                                            <Connector height={10}/>
                                            <TaskNode
                                              task={task}
                                              expanded={taskExpanded}
                                              hasSubs={hasSubs}
                                              onToggle={()=>toggle(task.id)}
                                            />
                                            {/* SUBTASKS */}
                                            {taskExpanded && hasSubs && (
                                              <>
                                                <Connector height={8}/>
                                                <HorizontalGroup>
                                                  {(task.subtasks||[]).map(st=>(
                                                    <TreeColumn key={st.id}>
                                                      <Connector height={8}/>
                                                      <SubtaskNode task={st}/>
                                                    </TreeColumn>
                                                  ))}
                                                </HorizontalGroup>
                                              </>
                                            )}
                                          </TreeColumn>
                                        );
                                      })}
                                    </HorizontalGroup>
                                  </>
                                )}
                              </TreeColumn>
                            );
                          })}
                        </HorizontalGroup>
                      </>
                    )}
                  </TreeColumn>
                );
              })}
            </HorizontalGroup>
          </>
        )}
      </div>

      </div>{/* end scroll wrapper */}

      {/* Legend */}
      <div style={{display:'flex',justifyContent:'center',marginTop:'40px'}}>
        <div style={{display:'flex',gap:'16px',alignItems:'center',background:'#fff',borderRadius:'12px',padding:'10px 20px',border:'1px solid #E2E8F0',flexWrap:'wrap',justifyContent:'center'}}>
          {[['🏢','Project','#5B5BD6'],['📁','Phase','#D97706'],['🎯','List','#0891B2'],['📋','Task','#16A34A']].map(([icon,label,color])=>(
            <div key={label} style={{display:'flex',alignItems:'center',gap:'5px'}}>
              <span style={{fontSize:'12px'}}>{icon}</span>
              <div style={{width:10,height:10,borderRadius:'2px',background:color}}/>
              <span style={{fontSize:'11px',color:'#64748B',fontWeight:'600'}}>{label}</span>
            </div>
          ))}
          <div style={{width:1,height:16,background:'#E2E8F0'}}/>
          {[['#DC2626','0–30%'],['#D97706','30–60%'],['#5B5BD6','60–100%'],['#16A34A','100%']].map(([c,l])=>(
            <div key={l} style={{display:'flex',alignItems:'center',gap:'4px'}}>
              <div style={{width:8,height:8,borderRadius:'50%',background:c}}/>
              <span style={{fontSize:'10px',color:'#8A93A6'}}>{l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Layout helpers — properly connected tree lines
function Connector({ height }) {
  return (
    <div style={{display:'flex',justifyContent:'center',width:'100%',flexShrink:0}}>
      <div style={{width:'2px',height,background:'#CBD5E1',flexShrink:0}}/>
    </div>
  );
}

// HorizontalGroup renders children in a row with a parent-to-children tree
// connector. The connector pieces live INSIDE each child's own cell, so they
// always line up with the real centre of that child — even when one sibling is
// expanded (and therefore much wider) and the others are collapsed. This avoids
// the misaligned/broken lines you get when a separate equal-flex bar row is laid
// over unequal-width columns.
function HorizontalGroup({ children }) {
  const kids = React.Children.toArray(children);
  if (!kids.length) return null;

  // Single child: the child's own Connector provides the vertical drop.
  if (kids.length === 1) {
    return (
      <div style={{display:'inline-flex',flexDirection:'column',alignItems:'center'}}>
        {kids[0]}
      </div>
    );
  }

  const BAR = '#CBD5E1';
  return (
    <div style={{display:'flex',alignItems:'flex-start'}}>
      {kids.map((kid, i) => {
        const isFirst = i === 0;
        const isLast = i === kids.length - 1;
        return (
          // Each cell shrink-wraps to its child's natural width (flex:0 0 auto),
          // so the half/full horizontal bar drawn here spans exactly to this
          // child's centre and meets its neighbours seamlessly.
          <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',flex:'0 0 auto'}}>
            {/* Horizontal connector band for this cell */}
            <div style={{display:'flex',width:'100%',height:'2px'}}>
              {/* left half: filled unless this is the first child */}
              <div style={{flex:1,height:'2px',background:isFirst?'transparent':BAR}}/>
              {/* right half: filled unless this is the last child */}
              <div style={{flex:1,height:'2px',background:isLast?'transparent':BAR}}/>
            </div>
            {/* The child column supplies its own vertical drop (Connector),
                which now meets this horizontal band exactly at the cell centre. */}
            {kid}
          </div>
        );
      })}
    </div>
  );
}

function TreeColumn({ children }) {
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',padding:'0 8px',flexShrink:0}}>
      {children}
    </div>
  );
}

// ── WBSNode with expand/collapse
function WBSNode({ id, label, progress, color, level, icon, sub, weight, expanded, hasChildren, onToggle, onClick }) {
  const pc = getProgressColor(progress);
  const sizes = {
    project: { w:210, pad:'16px 18px', r:'12px', ts:'15px', fw:'800', mh:'80px' },
    phase:   { w:185, pad:'13px 16px', r:'10px', ts:'13px', fw:'700', mh:'76px' },
    list:    { w:165, pad:'10px 13px', r:'8px',  ts:'12px', fw:'600', mh:'68px' },
  };
  const s = sizes[level] || sizes.list;
  const [hov, setHov] = useState(false);

  return (
    <div style={{position:'relative',display:'inline-flex',flexDirection:'column',alignItems:'stretch'}}>
      <div
        onClick={onClick}
        onMouseEnter={()=>setHov(true)}
        onMouseLeave={()=>setHov(false)}
        style={{
          width:s.w, minHeight:s.mh, maxHeight:'120px', padding:s.pad, borderRadius:s.r, overflow:'hidden',
          background:'#fff', border:`2px solid ${hov&&onClick?color:expanded?color:color+'50'}`,
          boxShadow: hov?`0 6px 20px ${color}25`:'0 2px 8px rgba(0,0,0,0.06)',
          cursor:onClick?'pointer':'default', transition:'all .2s',
          display:'flex', flexDirection:'column', gap:'6px',
        }}>
        <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
          <span style={{fontSize:level==='project'?'15px':'12px',flexShrink:0}}>{icon}</span>
          <span style={{fontSize:s.ts,fontWeight:s.fw,color:'#1A1D23',flex:1,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',lineHeight:1.3}} title={label}>{label}</span>
          {weight>0&&<span style={{fontSize:'9px',fontWeight:'700',padding:'1px 5px',borderRadius:'99px',background:`${color}18`,color,border:`1px solid ${color}30`,flexShrink:0}}>{weight}%</span>}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'5px'}}>
          <div style={{flex:1,height:5,background:'#EEF0F5',borderRadius:'99px',overflow:'hidden'}}>
            <div style={{height:'100%',width:`${Math.min(100,progress||0)}%`,background:pc,borderRadius:'99px',transition:'width .4s'}}/>
          </div>
          <span style={{fontSize:'10px',fontWeight:'700',color:pc,flexShrink:0,minWidth:'28px',textAlign:'right'}}>{Math.round(progress||0)}%</span>
        </div>
        {sub&&<span style={{fontSize:'10px',color:'#8A93A6'}}>{sub}</span>}
      </div>
      {/* Expand/collapse button */}
      {hasChildren && (
        <button onClick={e=>{e.stopPropagation();onToggle();}}
          style={{position:'absolute',bottom:-10,left:'50%',transform:'translateX(-50%)',width:20,height:20,borderRadius:'50%',background:expanded?color:'#fff',border:`2px solid ${color}`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',color:expanded?'#fff':color,zIndex:2,transition:'all .2s',padding:0,lineHeight:1}}>
          {expanded?'−':'+'}
        </button>
      )}
    </div>
  );
}

// ── Task node
function TaskNode({ task, expanded, hasSubs, onToggle }) {
  const color = STATUS_COLORS[task.status] || '#8A93A6';
  const progress = Math.min(100, task.calculatedProgress ?? task.progress ?? 0);
  return (
    <div style={{position:'relative'}}>
      <div style={{padding:'6px 10px',background:'#fff',border:`1.5px solid ${color}40`,borderRadius:'7px',width:'175px',minHeight:'60px',maxHeight:'110px',overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,0.06)',display:'flex',flexDirection:'column',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:'4px',marginBottom:'3px'}}>
          <span style={{fontSize:'10px',flexShrink:0}}>{task.task_type==='milestone'?'🏁':task.task_type==='meeting'?'📅':'📋'}</span>
          <div style={{fontSize:'10px',fontWeight:'600',color:'#374151',lineHeight:1.35,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical'}} title={task.name}>{task.name}</div>
        </div>
        <div style={{height:'3px',background:'#EEF0F5',borderRadius:'99px',overflow:'hidden'}}>
          <div style={{height:'100%',width:`${progress}%`,background:color,borderRadius:'99px'}}/>
        </div>
        <div style={{fontSize:'9px',color:color,fontWeight:'700',marginTop:'2px',textAlign:'right'}}>{Math.round(progress)}%</div>
      </div>
      {hasSubs && (
        <button onClick={e=>{e.stopPropagation();onToggle();}}
          style={{position:'absolute',bottom:-9,left:'50%',transform:'translateX(-50%)',width:17,height:17,borderRadius:'50%',background:expanded?color:'#fff',border:`2px solid ${color}`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'9px',color:expanded?'#fff':color,zIndex:2,padding:0,lineHeight:1}}>
          {expanded?'−':'+'}
        </button>
      )}
    </div>
  );
}

// ── Subtask node
function SubtaskNode({ task }) {
  const color = STATUS_COLORS[task.status] || '#8A93A6';
  const progress = Math.min(100, task.progress ?? 0);
  return (
    <div style={{padding:'4px 8px',background:'#F8F9FC',border:`1.5px solid ${color}30`,borderRadius:'6px',width:'150px',minHeight:'46px',display:'flex',flexDirection:'column',justifyContent:'space-between'}}>
      <div style={{fontSize:'9px',fontWeight:'600',color:'#374151',overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',lineHeight:1.3,marginBottom:'3px'}} title={task.name}>↳ {task.name}</div>
      <div style={{height:'2px',background:'#EEF0F5',borderRadius:'99px',overflow:'hidden'}}>
        <div style={{height:'100%',width:`${progress}%`,background:color,borderRadius:'99px'}}/>
      </div>
      <div style={{fontSize:'9px',color:color,fontWeight:'700',marginTop:'2px',textAlign:'right'}}>{Math.round(progress)}%</div>
    </div>
  );
}
